import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { newPropertiesData } from './NewProperties';
import heroBgImage from '../../assets/real.jpg';

const parsePriceToLakhs = (priceStr) => {
  if (!priceStr) return 0;
  const cleanStr = priceStr.toLowerCase().replace(/[^0-9.a-z]/g, '');
  const num = parseFloat(cleanStr.replace(/[a-z]/g, ''));

  if (cleanStr.includes('cr')) return num * 100;
  if (cleanStr.includes('k')) return num / 100;
  return num;
};

const dynamicProperties = newPropertiesData
  .map((p) => ({
    ...p,
    numericPrice: parsePriceToLakhs(p.price),
  }))
  .sort((a, b) => a.numericPrice - b.numericPrice);

const MIN_SLIDER_VAL = 10;
const highestPropertyPrice =
  dynamicProperties.length > 0
    ? dynamicProperties[dynamicProperties.length - 1].numericPrice
    : 20;
const roundedHighestPrice = Math.ceil(highestPropertyPrice);
const MAX_SLIDER_VAL = roundedHighestPrice + 5;
const SLIDER_RANGE = MAX_SLIDER_VAL - MIN_SLIDER_VAL;

const formatBudgetDisplay = (val) => {
  if (val >= 100) return `₹ ${(val / 100).toFixed(2)} Cr`;
  return `₹ ${val.toFixed(1)} L`;
};

const BRAND = {
  cyan: '#00AEEF',
  magenta: '#EC008C',
  red: '#ED1C24',
};

const STAT_BADGES = [
  {
    value: 'Kalyan',
    label: 'Prime Location',
    color: BRAND.cyan,
    icon: (
      <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
    ),
    extra: <circle cx="12" cy="10" r="3" />,
  },
  {
    value: '90%',
    label: 'Loan Assistance',
    color: BRAND.magenta,
    icon: <path d="M16 7h6v6" />,
    extra: <path d="m22 7-8.5 8.5-5-5L2 17" />,
  },
  {
    value: '100%',
    label: 'Authorized',
    color: BRAND.red,
    icon: (
      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
    ),
  },
];

const StatBadge = ({ value, label, color, icon, extra, delay }) => (
  <div
    className="hero-reveal group flex items-center gap-3 px-4 py-3 rounded-2xl bg-white/[0.06] border border-white/10 backdrop-blur-md hover:bg-white/[0.1] hover:border-white/20 transition-all duration-300 hover:-translate-y-1"
    style={{ animationDelay: `${delay}ms` }}
  >
    <div
      className="p-2 rounded-xl transition-shadow duration-300 group-hover:shadow-lg"
      style={{
        background: `${color}18`,
        boxShadow: `0 0 20px ${color}22`,
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {icon}
        {extra}
      </svg>
    </div>
    <div className="text-left">
      <p className="text-sm md:text-base font-bold leading-none text-white">{value}</p>
      <p className="text-[10px] md:text-xs text-white/45 mt-1 font-medium">{label}</p>
    </div>
  </div>
);

export default function HeroSection() {
  const navigate = useNavigate();
  const [budget, setBudget] = useState(roundedHighestPrice);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const t = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(t);
  }, []);

  const getMatchDetails = (currentBudget) => {
    const affordableProperties = dynamicProperties.filter(
      (p) => p.numericPrice <= currentBudget
    );

    if (affordableProperties.length === 0) {
      const lowestPrice =
        dynamicProperties.length > 0 ? dynamicProperties[0].numericPrice : 15.4;
      return {
        text: `Flats start at ${formatBudgetDisplay(lowestPrice)}.`,
        subtext: 'Ask about our 90% loan assistance!',
        canBuy: false,
        property: null,
      };
    }

    const bestMatch = affordableProperties[affordableProperties.length - 1];
    const shortLocation = bestMatch.location.split(',')[0];

    return {
      text: `You qualify for: ${bestMatch.type} in ${shortLocation}`,
      subtext: `${bestMatch.title} • ${bestMatch.price}`,
      canBuy: true,
      property: bestMatch,
    };
  };

  const match = getMatchDetails(budget);
  const sliderPercent = ((budget - MIN_SLIDER_VAL) / SLIDER_RANGE) * 100;

  const handleInquiry = () => {
    if (match.canBuy && match.property) {
      navigate(`/property/${match.property.id}`);
    } else {
      const whatsappNumber = '919702850777';
      const text = `Hello SES!%0A%0AI have a total budget of around ${formatBudgetDisplay(budget)}. Can you help me find funding options for a MHADA flat?`;
      window.open(`https://wa.me/${whatsappNumber}?text=${text}`, '_blank');
    }
  };

  return (
    <section className="relative z-30 isolate min-h-[85vh] md:min-h-[88vh] w-full text-white pb-40 md:pb-44 mb-40 md:mb-36 font-display">
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className={`absolute inset-0 bg-cover bg-center hero-bg-zoom transition-opacity duration-1000 ${
            mounted ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ backgroundImage: `url('${heroBgImage}')` }}
        />

        {/* Floating orbs — clipped inside background layer */}
        <div className="hero-orb hero-orb-cyan w-72 h-72 -top-20 -left-20 pointer-events-none" />
        <div className="hero-orb hero-orb-magenta w-96 h-96 top-1/4 -right-32 pointer-events-none" />
        <div className="hero-orb hero-orb-red w-64 h-64 bottom-32 left-1/4 pointer-events-none" />
      </div>

      {/* Overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950/90 via-slate-950/70 to-slate-950/95" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_15%_25%,rgba(0,174,239,0.18),transparent_50%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_85%_20%,rgba(236,0,140,0.14),transparent_45%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_100%,rgba(237,28,36,0.12),transparent_40%)]" />
      <div className="absolute inset-0 hero-grid-overlay pointer-events-none" />

      {/* Hero content */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-[60vh] md:min-h-[65vh] px-4 pt-20 pb-40 text-center">
        {/* Badge */}
        <div
          className={`hero-reveal inline-flex items-center gap-2.5 px-5 py-2 rounded-full border border-white/15 bg-white/[0.06] backdrop-blur-xl text-xs md:text-sm font-semibold tracking-wide mb-8 ${mounted ? '' : 'opacity-0'}`}
          style={{ animationDelay: '100ms' }}
        >
          <span className="relative flex h-2 w-2">
            <span
              className="hero-pulse-ring absolute inline-flex h-full w-full rounded-full opacity-75"
              style={{ background: BRAND.cyan }}
            />
            <span
              className="relative inline-flex rounded-full h-2 w-2"
              style={{ background: BRAND.cyan }}
            />
          </span>
          <span className="ses-brand-text font-bold">Authorized MHADA Agency</span>
        </div>

        {/* Headline */}
        <h1
          className={`max-w-5xl ${mounted ? '' : 'opacity-0'}`}
          style={{ animationDelay: '200ms' }}
        >
          <span
            className="hero-reveal block text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight uppercase text-white/90 mb-2"
            style={{ animationDelay: '200ms' }}
          >
            Own Your
          </span>
          <span
            className="hero-reveal block text-5xl sm:text-6xl md:text-8xl font-extrabold tracking-tighter uppercase leading-[0.95] mb-2"
            style={{ animationDelay: '350ms' }}
          >
            <span className="ses-brand-text drop-shadow-[0_0_40px_rgba(0,174,239,0.3)]">
              MHADA
            </span>
          </span>
          <span
            className="hero-reveal block text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight uppercase text-white"
            style={{ animationDelay: '500ms' }}
          >
            Dream Home
          </span>
        </h1>

        {/* Subtitle */}
        <p
          className={`hero-reveal text-sm md:text-lg text-white/60 mt-6 max-w-2xl leading-relaxed ${mounted ? '' : 'opacity-0'}`}
          style={{ animationDelay: '650ms' }}
        >
          Secure a 1 BHK in Shirdhon &amp; Khoni, Kalyan with just{' '}
          <span className="text-white font-semibold">10% down payment</span> and{' '}
          <span style={{ color: BRAND.cyan }} className="font-semibold">
            90% bank loan
          </span>{' '}
          assistance.
        </p>

        {/* Stats */}
        <div className="flex flex-wrap justify-center gap-3 md:gap-4 mt-10">
          {STAT_BADGES.map((badge, i) => (
            <StatBadge key={badge.label} {...badge} delay={800 + i * 120} />
          ))}
        </div>

        {/* Scroll hint */}
        <div
          className="hero-reveal mt-12 flex flex-col items-center gap-2 opacity-50"
          style={{ animationDelay: '1200ms' }}
        >
          <span className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-medium">
            Find your budget
          </span>
          <div className="w-5 h-8 rounded-full border-2 border-white/20 flex justify-center pt-1.5">
            <div
              className="w-1 h-2 rounded-full animate-bounce"
              style={{ background: BRAND.cyan }}
            />
          </div>
        </div>
      </div>

      {/* Budget calculator card */}
      <div
        className={`absolute left-1/2 bottom-0 -translate-x-1/2 w-[94%] max-w-4xl z-40 transition-all duration-700 ${
          mounted ? 'opacity-100 translate-y-1/2' : 'opacity-0 translate-y-[60%]'
        }`}
      >
        <div className="relative rounded-[1.75rem] p-[2px] ses-brand-gradient">
          <div className="relative rounded-[1.65rem] bg-slate-900/95 backdrop-blur-2xl hero-card-glow p-5 md:p-8 overflow-hidden">
            {/* Shimmer accent */}
            <div className="absolute inset-0 hero-shimmer pointer-events-none rounded-[1.65rem]" />

            <div className="relative flex flex-col md:flex-row items-stretch gap-6 md:gap-8">
              {/* Slider side */}
              <div className="w-full md:w-1/2">
                <div className="flex justify-between items-end mb-4">
                  <p className="text-xs font-bold text-white/50 uppercase tracking-widest">
                    My Total Budget
                  </p>
                  <p
                    className="text-2xl md:text-3xl font-black ses-brand-text transition-all duration-200"
                    key={budget}
                  >
                    {formatBudgetDisplay(budget)}
                  </p>
                </div>

                <div className="relative pt-1 pb-1">
                  <input
                    type="range"
                    min={MIN_SLIDER_VAL}
                    max={MAX_SLIDER_VAL}
                    step="0.1"
                    value={budget}
                    onChange={(e) => setBudget(parseFloat(e.target.value))}
                    className="hero-budget-slider w-full"
                    style={{
                      background: `linear-gradient(to right, ${BRAND.cyan} 0%, ${BRAND.magenta} ${sliderPercent * 0.7}%, ${BRAND.red} ${sliderPercent}%, rgba(255,255,255,0.12) ${sliderPercent}%, rgba(255,255,255,0.12) 100%)`,
                    }}
                  />
                </div>
                <div className="flex justify-between text-[10px] font-bold text-white/35 mt-2 px-0.5">
                  <span>{formatBudgetDisplay(MIN_SLIDER_VAL)}</span>
                  <span className="text-white/25">Drag to adjust</span>
                  <span>{formatBudgetDisplay(MAX_SLIDER_VAL)}</span>
                </div>
              </div>

              {/* Divider */}
              <div className="hidden md:block w-px self-stretch bg-gradient-to-b from-transparent via-white/15 to-transparent" />
              <div className="block md:hidden w-full h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />

              {/* Match side */}
              <div className="w-full md:w-1/2 flex flex-col sm:flex-row items-center justify-between gap-5">
                <div className="text-center sm:text-left flex-1 min-w-0">
                  <div className="flex items-center justify-center sm:justify-start gap-2 mb-2">
                    {match.canBuy ? (
                      <div
                        className="flex items-center justify-center w-7 h-7 rounded-full"
                        style={{ background: `${BRAND.cyan}20` }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke={BRAND.cyan}
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                          <path d="m9 11 3 3L22 4" />
                        </svg>
                      </div>
                    ) : (
                      <div
                        className="flex items-center justify-center w-7 h-7 rounded-full"
                        style={{ background: `${BRAND.magenta}20` }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke={BRAND.magenta}
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <circle cx="12" cy="12" r="10" />
                          <line x1="12" y1="8" x2="12" y2="12" />
                          <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                      </div>
                    )}
                    <p
                      className="text-sm font-bold"
                      style={{ color: match.canBuy ? BRAND.cyan : BRAND.magenta }}
                    >
                      {match.canBuy ? 'Match Found!' : 'Keep Exploring!'}
                    </p>
                  </div>
                  <p className="text-lg md:text-xl font-extrabold text-white leading-snug mb-1.5">
                    {match.text}
                  </p>
                  <p className="text-xs font-medium text-white/45 truncate">{match.subtext}</p>
                </div>

                <button
                  onClick={handleInquiry}
                  className="relative shrink-0 overflow-hidden group text-white text-sm px-7 py-4 rounded-2xl font-bold transition-all hover:-translate-y-1 active:translate-y-0 hover:shadow-[0_8px_30px_rgba(0,174,239,0.35)]"
                >
                  <span className="absolute inset-0 ses-brand-gradient group-hover:opacity-100 opacity-90 transition-opacity" />
                  <span className="relative flex items-center gap-2.5">
                    {match.canBuy ? 'Claim Flat' : 'Get Help'}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="transition-transform group-hover:translate-x-1"
                    >
                      <path d="M5 12h14" />
                      <path d="m12 5 7 7-7 7" />
                    </svg>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
